import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Paper,
} from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';

import { SelectField } from 'components/select-field/select-field';
import { SortOrder } from 'enums';

import { SearchBox } from './search-box';
import { useGeneralSearch } from './search-hooks';
import { SearchResult } from './search-result';
import { GeneralSearchItem } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleWrapper: {
      backgroundColor: '#626262',
      color: 'white',
      flexShrink: 0,
      padding: 25,
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
    },
    table: {
      margin: '25px 0',
    },
    tableContainer: {
      maxHeight: 'none',
    },
    resultContainer: {
      padding: '1rem 0',
    },
    noResults: {
      padding: '2rem 0',
    },
  })
);

const pageSizeOptions = [
  { id: '5', label: '5' },
  { id: '10', label: '10' },
  { id: '25', label: '25' },
];

const orderByOptions: { id: keyof GeneralSearchItem; label: string }[] = [
  { id: 'created', label: 'Datum vytvoření' },
  { id: 'archivalEntityType', label: 'Typ' },
  { id: 'timeRange', label: 'Časový rozsah' },
];

const orderOptions: { id: SortOrder; label: string }[] = [
  { id: SortOrder.DESC, label: 'Sestupně' },
  { id: SortOrder.ASC, label: 'Vzestupně' },
];

export function Search() {
  const classes = useStyles();

  const [searchedValue, setSearchedValue] = useState('');
  const {
    setPage,
    page,
    pageCount,
    items,
    pageSize,
    count,
    loading,
    setPageSize,
    orderBy,
    setOrderBy,
    order,
    setOrder,
  } = useGeneralSearch(searchedValue);

  const handleSearchedValue = useCallback(
    (value: string) => {
      setPage(1);
      setSearchedValue(value);
    },
    [setPage]
  );

  const handlePageChange = useCallback(
    (_event: unknown, page: number) => setPage(page),
    [setPage]
  );

  const resultCount = useMemo(() => {
    const start = (page - 1) * pageSize + 1;

    return `${start} - ${start - 1 + items.length} ze ${count}`;
  }, [count, items.length, page, pageSize]);

  const handlePageSizeChange = useCallback(
    (pageSizeStr: string) => {
      const pageSize = parseInt(pageSizeStr);

      if (isNaN(pageSize)) {
        return;
      }

      setPageSize(pageSize);
      setPage(1);
    },
    [setPage, setPageSize]
  );

  const handleSetOrderBy = useCallback(
    (orderBy: string) => {
      setOrderBy(orderBy);
      setPage(1);
    },
    [setOrderBy, setPage]
  );

  const handleSetOrder = useCallback(
    (order: string) => {
      setOrder(order as SortOrder);
      setPage(1);
    },
    [setOrder, setPage]
  );

  return (
    <div>
      <Grid container className={classes.titleWrapper}>
        <Grid item xs={1} md={2} />
        <Grid item xs={10} md={8} className={classes.title}>
          <Typography variant="h1" align="left">
            Hledat
          </Typography>
          <SearchBox handleSearch={handleSearchedValue} />
        </Grid>
        <Grid item xs={1} md={2} />
      </Grid>
      <Grid container>
        <Grid item xs={1} md={2} />
        <Grid item xs={10} md={8} className={classes.table}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={6}>
              Výsledky hledání: {count && !loading ? resultCount : '0 / 0'}
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectField
                label="Výsledků na stránku"
                name="pageSize"
                value={pageSize.toString()}
                onChange={handlePageSizeChange}
                items={pageSizeOptions}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectField
                label="Řadit podle"
                name="orderBy"
                value={orderBy}
                onChange={handleSetOrderBy}
                items={orderByOptions}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectField
                label="Uspořádání"
                name="order"
                value={order}
                onChange={handleSetOrder}
                items={orderOptions}
              />
            </Grid>
          </Grid>
          <Box className={classes.resultContainer}>
            {loading ? (
              <LinearProgress />
            ) : !count ? (
              <Paper elevation={0}>
                <Typography
                  variant="h4"
                  align="center"
                  className={classes.noResults}
                >
                  žádné výsledky...
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid item key={item.label} xs={12}>
                    <SearchResult key={item.label} item={item} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          <Box display="flex" justifyContent="center">
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
            />
          </Box>
        </Grid>
        <Grid item xs={1} md={2} />
      </Grid>
    </div>
  );
}
